import { memo, useState } from "react";
import prettyBytes from "pretty-bytes";
import { ArrowPathIcon, CheckCircleIcon, ChevronDownIcon, ClockIcon, ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { SecondaryButton } from "../../components/general/SecondaryButton.tsx";
import type { DocumentDirection } from "../../generated/enums.ts";
import type { UploadQueueError, UploadQueueItem, UploadQueueStatus } from "./useUploadQueue.ts";

const directionLabels: Record<DocumentDirection, string> = {
    purchase_invoice: 'Inkoop',
    sales_invoice: 'Verkoop',
    other: 'Overig',
};

const errorLabels: Record<UploadQueueError, string> = {
    rejected: 'Bestand geweigerd',
    upload_failed: 'Uploaden mislukt',
};

const statusIcons: Record<UploadQueueStatus, JSX.Element> = {
    queued: <ClockIcon className="size-4 shrink-0 text-gray-300"/>,
    uploading: <ArrowPathIcon className="size-4 shrink-0 text-primary animate-spin"/>,
    done: <CheckCircleIcon className="size-4 shrink-0 text-emerald-500"/>,
    failed: <XMarkIcon className="size-4 shrink-0 text-red-500"/>,
};

const statusLabels: Record<UploadQueueStatus, string> = {
    queued: 'In wachtrij',
    uploading: 'Uploaden',
    done: 'Gedeeld',
    failed: 'Mislukt',
};

export function describeTimeLeft(secondsLeft: number | null): string | null {
    if (secondsLeft === null) return null;
    if (secondsLeft < 60) return 'bijna klaar';
    const minutes = Math.ceil(secondsLeft / 60);
    return `nog ongeveer ${minutes} ${minutes === 1 ? 'minuut' : 'minuten'}`;
}

interface UploadQueueRowProps {
    item: UploadQueueItem;
    onRetry: (id: string) => void;
}

export const UploadQueueRow = memo(function UploadQueueRow({ item, onRetry }: UploadQueueRowProps) {
    const name = item.files.map((file) => file.name).join(' + ');
    const size = item.files.reduce((total, file) => total + file.size, 0);
    return (
        <li className="flex items-center gap-3 px-6 py-2 text-sm">
            {statusIcons[item.status]}
            <span className="sr-only">{statusLabels[item.status]}:</span>
            <div className="min-w-0 flex-1">
                <div className="truncate text-gray-800" title={name}>{name}</div>
                {item.error && <div className="text-xs text-red-600">{errorLabels[item.error]}</div>}
            </div>
            <span className="shrink-0 text-xs text-gray-400">{prettyBytes(size)}</span>
            {item.status === 'failed' && (
                <button type="button" className="shrink-0 font-medium text-primary hover:underline"
                        aria-label={`Opnieuw: ${name}`} onClick={() => onRetry(item.id)}>Opnieuw</button>
            )}
        </li>
    );
});

export interface UploadQueueSummary {
    doneCount: number;
    failedItems: UploadQueueItem[];
    settledCount: number;
    isRunning: boolean;
    directionLabel: string | null;
}

export function summarizeQueue(items: UploadQueueItem[]): UploadQueueSummary {
    const doneCount = items.filter((item) => item.status === 'done').length;
    const failedItems = items.filter((item) => item.status === 'failed');
    const settledCount = doneCount + failedItems.length;
    const directions = new Set(items.map((item) => item.direction));
    return {
        doneCount,
        failedItems,
        settledCount,
        isRunning: settledCount < items.length,
        directionLabel: directions.size === 1 ? directionLabels[[...directions][0]] : null,
    };
}

interface UploadQueueListProps {
    items: UploadQueueItem[];
    secondsLeft: number | null;
    isStalled: boolean;
    onResume: () => void;
    onRetry: (id: string) => void;
    onRetryAllFailed: () => void;
    onClearFinished: () => void;
}

export function UploadQueueList({ items, secondsLeft, isStalled, onResume, onRetry, onRetryAllFailed, onClearFinished }: UploadQueueListProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    if (items.length === 0) return null;

    const { doneCount, failedItems, settledCount, isRunning, directionLabel } = summarizeQueue(items);
    const visibleItems = isExpanded ? items : (isRunning ? [] : failedItems);

    const closeButton = (
        <button type="button" className="shrink-0 text-sm text-gray-400 hover:text-gray-700"
                onClick={() => { setIsExpanded(false); onClearFinished(); }}>Sluiten</button>
    );

    const toggle = (
        <button type="button" className="flex shrink-0 items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-800"
                onClick={() => setIsExpanded(!isExpanded)} aria-expanded={isExpanded}>
            {isExpanded ? 'Verberg bestanden' : 'Toon bestanden'}
            <ChevronDownIcon className={`size-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}/>
        </button>
    );

    return (
        <div className={`mt-5 overflow-hidden rounded-2xl border bg-white shadow-sm ${!isRunning && failedItems.length > 0 ? 'border-amber-200' : 'border-gray-200'}`}>
            <div className="px-6 py-4">
                {isRunning && isStalled ? (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <ExclamationTriangleIcon className="size-6 shrink-0 text-amber-500"/>
                        <div className="flex-1">
                            <span className="font-semibold text-gray-900">Verbinding verloren</span>
                            <span className="text-gray-500"> · {settledCount} van {items.length} gedeeld, de rest wacht</span>
                        </div>
                        <SecondaryButton title="Opnieuw proberen" onClick={onResume}/>
                        {toggle}
                    </div>
                ) : isRunning ? (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <ArrowPathIcon className="size-5 shrink-0 text-primary animate-spin"/>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2">
                                <span className="font-semibold text-gray-900">Uploaden… {settledCount} van {items.length}</span>
                                <span className="truncate text-sm text-gray-400">
                                    {[directionLabel, describeTimeLeft(secondsLeft)].filter(Boolean).join(' · ')}
                                </span>
                            </div>
                            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100" role="progressbar"
                                 aria-label="Uploadvoortgang" aria-valuemin={0} aria-valuemax={items.length} aria-valuenow={settledCount}>
                                <div className="h-full rounded-full bg-primary transition-[width] duration-300"
                                     style={{ width: `${Math.round((settledCount / items.length) * 100)}%` }}/>
                            </div>
                        </div>
                        {failedItems.length > 0 && (
                            <span className="shrink-0 rounded-full bg-amber-50 px-2.5 py-0.5 text-sm text-amber-600">{failedItems.length} mislukt</span>
                        )}
                        {toggle}
                    </div>
                ) : failedItems.length > 0 ? (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <ExclamationTriangleIcon className="size-6 shrink-0 text-amber-500"/>
                        <div className="flex-1">
                            <span className="font-semibold text-gray-900">{doneCount} van {items.length} gedeeld</span>
                            <span className="text-gray-500"> · {failedItems.length} mislukt</span>
                        </div>
                        <SecondaryButton title="Opnieuw proberen" onClick={onRetryAllFailed}/>
                        {toggle}
                        {closeButton}
                    </div>
                ) : (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <CheckCircleIcon className="size-6 shrink-0 text-emerald-500"/>
                        <div className="flex-1">
                            <span className="font-semibold text-gray-900">{doneCount} {doneCount === 1 ? 'document' : 'documenten'} gedeeld</span>
                            <span className="text-gray-500"> · we verwerken {doneCount === 1 ? 'het' : 'ze'} nu, dat kan even duren</span>
                        </div>
                        {toggle}
                        {closeButton}
                    </div>
                )}
                {isRunning && (
                    <p className="mt-3 text-xs text-gray-400">Je kunt meer bestanden toevoegen en verder werken in de app; sluit alleen dit tabblad niet tot het uploaden klaar is.</p>
                )}
            </div>
            {visibleItems.length > 0 && (
                <ul className="max-h-[24rem] divide-y divide-gray-100 overflow-y-auto border-t border-gray-100">
                    {visibleItems.map((item) => (
                        <UploadQueueRow key={item.id} item={item} onRetry={onRetry}/>
                    ))}
                </ul>
            )}
        </div>
    );
}
